import { useContext, useEffect, createContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from './firebase/firebase';
import { signInWithEmailAndPassword, onAuthStateChanged, signOut } from 'firebase/auth'

const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  });

  // Firebase Auth 토큰 복원이 끝났는지 여부. localStorage의 user는 동기적으로
  // 복원되지만 실제 인증 토큰은 비동기로 복원되므로, 데이터 조회는 이 값이
  // true가 된 뒤에 시작해야 권한 거부(레이스)가 발생하지 않는다.
  const [authReady, setAuthReady] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (!firebaseUser) {
        // 저장된 user는 있는데 Firebase 세션이 사라진 경우(만료 등):
        // 데이터 조회 시 권한 거부가 나므로 로그인 화면으로 보낸다.
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          console.warn('Firebase 인증 세션이 없어 재로그인이 필요합니다.');
          localStorage.removeItem('user');
          setUser(null);
          navigate('/login');
        }
      }
      setAuthReady(true);
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loginAction = async (id, pwd, checked) => {
    if (id === "" || pwd === "") return;

    if (checked) {
      localStorage.setItem('id', id);
      localStorage.setItem('checked', checked);
    } else {
      localStorage.removeItem('id');
      localStorage.removeItem('checked');
    }

    try {
      const email = `${id}@neurocare.com`;
      const userCredential = await signInWithEmailAndPassword(auth, email, pwd);
      const uid = userCredential.user.uid;

      // 인증된 계정의 실제 이메일에서 원본 ID를 가져온다.
      // Firebase 인증은 대소문자를 구분하지 않으므로, 입력값이 계정 원본 ID와
      // 대소문자까지 정확히 일치하지 않으면 로그인을 거부한다.
      const authedId = userCredential.user.email.split("@")[0];
      if (id !== authedId) {
        await signOut(auth);
        alert('사업자정보, 아이디 혹은 패스워드가 잘못되었습니다.');
        return;
      }

      const loggedInUser = { id: authedId, uid };
      setUser(loggedInUser);
      localStorage.setItem('user', JSON.stringify(loggedInUser));
      navigate("/");
    } catch (error) {
      console.error('로그인 실패:', error);
      alert('사업자정보, 아이디 혹은 패스워드가 잘못되었습니다.');
    }
  };

  const logOut = () => {
    setUser(null);
    localStorage.removeItem('user');
    navigate("/login");
  };

  return (
    <AuthContext.Provider value={{ user, authReady, loginAction, logOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;

export const useAuth = () => {
  return useContext(AuthContext);
};